import React, { useEffect, useState } from "react";
import { X, Upload, IndianRupee, Phone, CreditCard } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  addEpcPayment,
  getDetailsOfRegisteredUserWithPaymentDetails,
} from "../../store/slices/epcSlice";
import toast from "react-hot-toast";
import { usePreventWheelChange } from "../../utils/usePreventWheelChange";

interface AddPaymentFormProps {
  onClose: () => void;
  customerMobileNumber?: string;
  remainingAmount?: number;
  pendingPaymentAmount?: number;

  currentPage?: number;
  searchTerm?: string;
  selectedSalesRep?: string;
  selectedCSE?: string;
  startDate?: string;
  endDate?: string;
  panelBrand?: string;
  plantSize?: string;
  registrationStatus?: string;
}

const AddPaymentForm: React.FC<AddPaymentFormProps> = ({
  onClose,
  customerMobileNumber: initialMobileNumber = "",
  remainingAmount,
  pendingPaymentAmount = 0,

  currentPage = 1,
  searchTerm = "",
  selectedSalesRep,
  selectedCSE,
  startDate,
  endDate,
  panelBrand,
  plantSize,
  registrationStatus,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const { addingPaymentLoading } = useSelector(
    (state: RootState) => state.epc.loadingState,
  );

  const [formData, setFormData] = useState({
    customerMobileNumber: initialMobileNumber,
    amount: "",
  });
  const [file, setFile] = useState<File | null>(null);
  const [isNetMeterCharge, setIsNetMeterCharge] = useState(false);
  // const selectedSalesRep = useSelector(
  //   (state: RootState) => state.epc.selectedSalesRep,
  // );
  // const selectedCseID = useSelector(
  //   (state: RootState) => state.epc.selectedCseIdForCustomerDetails,
  // );

  const handleWheelPrevent = usePreventWheelChange();
  const enteredAmount = Number(formData.amount);
  const hasExceededRemaining =
    formData.amount.trim() !== "" &&
    remainingAmount !== undefined &&
    enteredAmount > Math.max(remainingAmount, 0);
  const requiresNetMeterCharge =
    hasExceededRemaining && (remainingAmount ?? 0) > 0;
  const netMeterPaymentAmount = hasExceededRemaining
    ? enteredAmount - Math.max(remainingAmount ?? 0, 0)
    : 0;

  useEffect(() => {
    if (!hasExceededRemaining) setIsNetMeterCharge(false);
  }, [hasExceededRemaining]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        setFile(null);
        return;
      }

      // Check file type
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "application/pdf",
      ];
      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error("Only JPG, PNG, and PDF files are allowed");
        setFile(null);
        return;
      }

      setFile(selectedFile);
    }
  };

  const validateForm = () => {
    let isValid = true;

    if (!formData.customerMobileNumber.trim()) {
      toast.error("Mobile number is required");
      isValid = false;
    } else if (!/^\d{10}$/.test(formData.customerMobileNumber)) {
      toast.error("Please enter a 10 digit mobile number");
      isValid = false;
    }

    if (!formData.amount.trim()) {
      toast.error("Amount is required");
    } else if (isNaN(Number(formData.amount)) || Number(formData.amount) <= 0) {
      toast.error("Please enter a valid amount");
      isValid = false;
    }

    if (!file) {
      toast.error("Payment proof is required");
      isValid = false;
    }

    if (requiresNetMeterCharge && !isNetMeterCharge) {
      toast.error("Select the net meter charges option for the excess amount");
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !file) return;

    try {
      await dispatch(
        addEpcPayment({
          customerMobileNumber: formData.customerMobileNumber,
          amount:
            hasExceededRemaining && isNetMeterCharge
              ? enteredAmount - netMeterPaymentAmount
              : enteredAmount,
          ...(hasExceededRemaining &&
            isNetMeterCharge && {
              netMeterPayment: netMeterPaymentAmount,
            }),
          file,
        }),
      ).unwrap();

      onClose();
      await dispatch(
        getDetailsOfRegisteredUserWithPaymentDetails({
          salesRepId: selectedSalesRep,
          cseID: selectedCSE,
          search: searchTerm,
          page: currentPage,
          startDate: startDate,
          endDate: endDate,
          panelBrand: panelBrand,
          plantSize: plantSize,
          registrationStatus: registrationStatus,
        }),
      );
    } catch (error) {
      console.error("Error adding payment:", error);
    }
  };

  return (
    <div className="fixed inset-0 z-[150] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-blue-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Add Payment
              </h3>
              <p className="text-sm text-gray-500">
                Record customer payment securely
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Customer */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Customer Mobile Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                name="customerMobileNumber"
                value={formData.customerMobileNumber}
                disabled
                className="w-full pl-10 pr-3 py-2.5 rounded-lg border bg-gray-100 text-gray-700 cursor-not-allowed"
              />
            </div>
          </div>

          <div
            className={`rounded-xl border px-4 py-3 ${
              remainingAmount !== undefined && remainingAmount <= 0
                ? "border-emerald-200 bg-emerald-50"
                : "border-amber-200 bg-amber-50"
            }`}
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <p
                  className={`text-xs font-semibold ${
                    remainingAmount !== undefined && remainingAmount <= 0
                      ? "text-emerald-700"
                      : "text-amber-700"
                  }`}
                >
                  Remaining balance
                </p>
                <p className="mt-0.5 text-xs text-gray-500">
                  After approved and pending payments, round-off, and adjustments
                </p>
                {pendingPaymentAmount > 0 && (
                  <p className="mt-1 text-xs font-medium text-amber-700">
                    Includes ₹
                    {pendingPaymentAmount.toLocaleString("en-IN", {
                      maximumFractionDigits: 2,
                    })}{" "}
                    pending
                  </p>
                )}
              </div>
              <p
                className={`shrink-0 text-lg font-bold ${
                  remainingAmount !== undefined && remainingAmount <= 0
                    ? "text-emerald-800"
                    : "text-amber-900"
                }`}
              >
                {remainingAmount === undefined
                  ? "Unavailable"
                  : `₹${remainingAmount.toLocaleString("en-IN", {
                      maximumFractionDigits: 2,
                    })}`}
              </p>
            </div>
          </div>

          {/* Amount */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Payment Amount
            </label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                onWheel={handleWheelPrevent}
                placeholder="Enter amount"
                className="w-full pl-10 pr-3 py-2.5 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
          </div>

          {hasExceededRemaining && (
            <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={isNetMeterCharge}
                  onChange={(e) => setIsNetMeterCharge(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-blue-600"
                />
                <span>
                  <span className="block text-sm font-semibold text-gray-800">
                    Extra cost as net meter charges
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-600">
                    The entered amount is ₹
                    {enteredAmount.toLocaleString("en-IN", {
                      maximumFractionDigits: 2,
                    })}, which exceeds the remaining balance by ₹
                    {netMeterPaymentAmount.toLocaleString("en-IN", {
                      maximumFractionDigits: 2,
                    })}
                    .
                  </span>
                </span>
              </label>

              <div className="grid grid-cols-1 gap-2 border-t border-amber-200 pt-3 text-xs sm:grid-cols-3">
                <div className="rounded-lg bg-white/70 p-3">
                  <p className="text-gray-500">Regular payment</p>
                  <p className="mt-1 font-bold text-gray-800">
                    ₹
                    {(enteredAmount - netMeterPaymentAmount).toLocaleString(
                      "en-IN",
                      { maximumFractionDigits: 2 },
                    )}
                  </p>
                </div>
                <div className="rounded-lg bg-white/70 p-3">
                  <p className="text-gray-500">Net meter charges</p>
                  <p className="mt-1 font-bold text-gray-800">
                    ₹
                    {netMeterPaymentAmount.toLocaleString("en-IN", {
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
                <div className="rounded-lg bg-white/70 p-3">
                  <p className="text-gray-500">Entered total</p>
                  <p className="mt-1 font-bold text-gray-800">
                    ₹
                    {enteredAmount.toLocaleString("en-IN", {
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
              </div>
              {!isNetMeterCharge && requiresNetMeterCharge && (
                <p className="text-xs font-medium text-amber-800">
                  Select the checkbox to submit this split.
                </p>
              )}
              {!isNetMeterCharge && !requiresNetMeterCharge && (
                <p className="text-xs text-gray-600">
                  Optional: select the checkbox to record this amount as net
                  meter charges.
                </p>
              )}
            </div>
          )}

          {/* Upload */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Payment Proof
            </label>

            <label className="flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer hover:border-blue-400 transition">
              <Upload className="w-10 h-10 text-gray-400 mb-2" />
              <p className="text-sm text-gray-600">Click to upload</p>
              <p className="text-xs text-gray-400 mt-1">
                JPG, PNG or PDF (max 5MB)
              </p>
              <input
                type="file"
                className="hidden"
                onChange={handleFileChange}
                accept=".jpg,.jpeg,.png,.pdf"
              />
            </label>

            {file && (
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                <span className="text-sm text-green-700 truncate">
                  {file.name}
                </span>
                <span className="text-xs text-green-600">Selected</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addingPaymentLoading}
              className="px-5 py-2 text-sm rounded-lg bg-navyBlue-primary text-white hover:bg-navyBlue-light disabled:opacity-50 transition-all"
            >
              {addingPaymentLoading ? "Processing..." : "Confirm Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPaymentForm;
